import { useEffect, useState } from "react";

import { parseAmount, toNumber } from "../lib/format";

/**
 * Menge als „− 2 +“ – gleich in Artikelliste, Artikelseite und Warenkorb.
 * Mit `onSet` lässt sich die Zahl auch eintippen (Enter oder Verlassen
 * übernimmt); ohne ist sie nur Anzeige.
 */
export default function QtyStepper({
  value,
  label,
  onLess,
  onMore,
  onSet,
  lessLabel,
  disabled = false,
  lessDisabled = false,
  size = "md",
}: {
  value: string | number;
  /** z. B. „Menge ACKH3/4“ */
  label: string;
  onLess: () => void;
  onMore: () => void;
  onSet?: (next: number) => void;
  lessLabel?: string;
  disabled?: boolean;
  lessDisabled?: boolean;
  size?: "sm" | "md";
}) {
  const shown = (toNumber(value) ?? 0).toLocaleString("de-AT", { maximumFractionDigits: 3, useGrouping: false });
  const [draft, setDraft] = useState(shown);
  useEffect(() => setDraft(shown), [shown]);
  const commit = () => {
    const next = parseAmount(draft);
    if (onSet && next && next > 0 && next !== toNumber(value)) onSet(next);
    else setDraft(shown);
  };
  const step = "inline-flex h-full w-7 items-center justify-center text-base leading-none text-slate-600 hover:bg-emerald-100 hover:text-slate-900 disabled:opacity-40";
  return (
    <span
      role="group"
      aria-label={label}
      className={`inline-flex items-center overflow-hidden rounded-md border border-emerald-200 bg-emerald-50 ${size === "sm" ? "h-8" : "h-9"}`}
      onClick={(event) => event.stopPropagation()}
    >
      <button type="button" className={step} disabled={disabled || lessDisabled} onClick={onLess} aria-label={lessLabel ?? `${label}: eins weniger`}>
        −
      </button>
      {onSet ? (
        <input
          value={draft}
          inputMode="decimal"
          disabled={disabled}
          aria-label={label}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") commit();
            if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
            // Arrows change the typed number; leaving the field saves it.
            event.preventDefault();
            const current = parseAmount(draft) ?? toNumber(value);
            if (current === null) return;
            const next = current + (event.key === "ArrowUp" ? 1 : -1);
            if (next > 0) setDraft(next.toLocaleString("de-AT", { maximumFractionDigits: 3, useGrouping: false }));
          }}
          className="h-full w-12 border-x border-emerald-200 bg-white text-center text-sm font-semibold tabular-nums text-emerald-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-emerald-500/30"
        />
      ) : (
        <span className="min-w-8 px-1 text-center text-sm font-semibold tabular-nums text-emerald-800" aria-live="polite" title="Menge im Warenkorb">
          {shown}
        </span>
      )}
      <button type="button" className={step} disabled={disabled} onClick={onMore} aria-label={`${label}: eins mehr`}>
        +
      </button>
    </span>
  );
}
